import React from 'react'
import { UpsellActivationDialog } from './dialog/upsell/UpsellActivationDialog'
import { UpsellDowngradePlanDialog } from './dialog/upsell/UpsellDowngradePlanDialog'
import UpsellCancellationWarningDialog from './dialog/upsell/UpsellCancellationWarningDialog'
import { type UpsellDialogsProps, type UpsellTierRestrictions, UpsellDialogKey } from 'src/lib/types/appsIntegrations'
import { sumFeaturePriceFromTiers } from 'src/lib/utils/appsIntegrationsHelpers'
import { useUpsellPlan } from 'src/lib/hooks/useUpsellPlan'

// This is basically an orchrestrator component that handles the dialog components related to upsell. Similarly for other apps - we will have their dialogs in a single folder and one single orchrestrator to handle the dialogs

export const UpsellDialogs: React.FC<UpsellDialogsProps> = ({
  state,
  upsells = [],
  activeUpsellsCount = 0,
  onDegradeContinue = () => {}
}) => {
  const {
    currentPlan,
    isLoading,
    activeDialog,
    planModalEntryPoint,
    isPlanModalReopened,
    selectingPlanId,
    closeDialog,
    handleSelectPlan,
    handleContinueCancellation,
    handleSwitchToLite,
    handleDowngradeBack
  } = state

  // Static Upsell config (plan tiers, dialog copy) comes from shopfloApps via the hook; `state` only carries live state and actions.
  const {
    state: { upsellPlans, upsellCancellationConfig, upsellCosmetics }
  } = useUpsellPlan()

  const upsellLitePlanConfig = upsellPlans.find(item => item.id === 'lite')

  const liteRestrictions = (upsellLitePlanConfig?.restrictions ?? {}) as UpsellTierRestrictions
  const maxAllowedUpsellsOnLite = liteRestrictions.MAX_ACTIVE_UPSELL_GROUPS ?? 3

  // Sum of the tier's feature prices — same as the plan cards, and independent of the feature key names.
  const litePlanPrice = sumFeaturePriceFromTiers(upsellLitePlanConfig?.feature_price_mapping) ?? 1499

  return (
    <>
      <UpsellActivationDialog
        state={{
          isOpen: activeDialog === UpsellDialogKey.plans,
          appName: upsellCosmetics.name,
          logoUrl: upsellCosmetics.logoUrl,
          isLoading,
          currentPlan,
          plans: upsellPlans,
          loadingPlanId: selectingPlanId,
          entryPoint: planModalEntryPoint,
          isReopened: isPlanModalReopened
        }}
        actions={{ onClose: closeDialog, onSelectPlan: handleSelectPlan }}
      />

      <UpsellCancellationWarningDialog
        state={{
          isOpen: activeDialog === UpsellDialogKey.cancellationWarning,
          isLoading,
          currentPlan,
          litePlanPrice,
          config: upsellCancellationConfig
        }}
        actions={{ onClose: closeDialog, onContinue: handleContinueCancellation, onSwitchToLite: handleSwitchToLite }}
      />

      <UpsellDowngradePlanDialog
        state={{
          isOpen: activeDialog === UpsellDialogKey.degradePlan,
          upsells,
          activeUpsellsCount,
          isLoading,
          maxAllowedActiveUpsellsOnLite: maxAllowedUpsellsOnLite
        }}
        actions={{ onBack: handleDowngradeBack, onClose: closeDialog, onContinue: onDegradeContinue }}
      />
    </>
  )
}

export default UpsellDialogs
